import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  User,
  Mail,
  Phone,
  Key,
  Shield,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle2,
  Building2,
  Percent,
  Info,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useQuery } from "@tanstack/react-query";
import * as engineService from "@/shared/api/services/engineService";

export default function EngineSettings() {
  const resellerName = "Gaming Partners Ltd";
  const { t } = useLanguage();

  const { data: engineProfile } = useQuery({
    queryKey: ["engine-profile"],
    queryFn: () => engineService.getProfile(),
  });

  // Mock reseller data
  const [reseller, setReseller] = useState({
    name: "Gaming Partners Ltd",
    code: "RSL001",
    email: "admin@gamingpartners.com",
    support_email: "support@gamingpartners.com",
    contact_phone: "+66 98 765 4321",
    api_key: "rsl_pk_live_a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6",
    secret_key: "rsl_sk_live_x9y8z7w6v5u4t3s2r1q0p9o8n7m6l5k4",
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [regenerateDialog, setRegenerateDialog] = useState(false);
  const [copied, setCopied] = useState(null);
  const [saving, setSaving] = useState(false);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleRegenerate = () => {
    // Mock regenerate
    const newKey = "rsl_pk_live_" + Array.from({ length: 32 }, () =>
      "abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(Math.random() * 36)]
    ).join("");
    const newSecret = "rsl_sk_live_" + Array.from({ length: 32 }, () =>
      "abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(Math.random() * 36)]
    ).join("");
    setReseller(prev => ({ ...prev, api_key: newKey, secret_key: newSecret }));
    setRegenerateDialog(false);
  };

  const handleSave = async () => {
    setSaving(true);
    // Simulate save
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
  };

  return (
    <EngineLayout currentPage="EngineSettings" resellerName={resellerName}>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("settings")}</h1>
          <p className="text-slate-500">{t("manageProfile")}</p>
        </div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {t("resellerProfile")}
              </CardTitle>
              <CardDescription>{t("yourBusinessInfo")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("businessName")}</Label>
                  <Input
                    value={reseller.name}
                    onChange={(e) => setReseller(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("resellerCode")}</Label>
                  <Input value={reseller.code} disabled className="bg-slate-50" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("email")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="email"
                      value={reseller.email}
                      onChange={(e) => setReseller(prev => ({ ...prev, email: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("supportEmail")}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="email"
                      value={reseller.support_email}
                      onChange={(e) => setReseller(prev => ({ ...prev, support_email: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("contactPhone")}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={reseller.contact_phone}
                    onChange={(e) => setReseller(prev => ({ ...prev, contact_phone: e.target.value }))}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={handleSave} disabled={saving} className="bg-sky-600 hover:bg-sky-700">
                  {saving ? t("saving") : t("saveChanges")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* API Keys Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                {t("apiCredentialsTitle")}
              </CardTitle>
              <CardDescription>{t("yourApiKeysIntegration")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Security Notice */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex gap-3">
                  <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-900">{t("securityNotice")}</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      {t("keepSecretConfidential")} {t("neverExposeSecretKey")}
                    </p>
                  </div>
                </div>
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label>{t("apiKeyPublic")}</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-sm text-white overflow-x-auto">
                    {showApiKey ? reseller.api_key : "••••••••••••••••••••••••••••••••••••••••"}
                  </div>
                  <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)}>
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(reseller.api_key, "api")}>
                    {copied === "api" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Secret Key */}
              <div className="space-y-2">
                <Label>{t("secretKey")}</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-sm text-white overflow-x-auto">
                    {showSecretKey ? reseller.secret_key : "••••••••••••••••••••••••••••••••••••••••"}
                  </div>
                  <Button variant="outline" size="icon" onClick={() => setShowSecretKey(!showSecretKey)}>
                    {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(reseller.secret_key, "secret")}>
                    {copied === "secret" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Fees & Commission Card */}
        {engineProfile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="h-5 w-5" />
                  {t("feesAndCommission")}
                </CardTitle>
                <CardDescription>{t("feesAndCommissionDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Parent Commission Info */}
                {engineProfile.parentName && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex gap-3">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-900 mb-2">
                          {t("givenBy").replace("{type}", engineProfile.parentType).replace("{name}", engineProfile.parentName)}
                        </h4>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-blue-700 font-medium">{t("depositCommission")}</p>
                            <p className="text-blue-900 text-lg font-semibold">
                              {engineProfile.parentDepositCommission !== null && engineProfile.parentDepositCommission !== undefined
                                ? `${engineProfile.parentDepositCommission}%`
                                : "N/A"}
                            </p>
                          </div>
                          <div>
                            <p className="text-blue-700 font-medium">{t("withdrawalCommission")}</p>
                            <p className="text-blue-900 text-lg font-semibold">
                              {engineProfile.parentWithdrawalCommission !== null && engineProfile.parentWithdrawalCommission !== undefined
                                ? `${engineProfile.parentWithdrawalCommission}%`
                                : "N/A"}
                            </p>
                          </div>
                          <div>
                            <p className="text-blue-700 font-medium">{t("percentageFee")}</p>
                            <p className="text-blue-900 text-lg font-semibold">
                              {engineProfile.parentPercentageFee !== null && engineProfile.parentPercentageFee !== undefined
                                ? `${engineProfile.parentPercentageFee}%`
                                : "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Commission Rates (Read-only) */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">{t("yourCommissionRates")}</Label>
                    <p className="text-xs text-slate-500 mt-1">{t("yourCommissionRatesDesc")}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">{t("depositCommissionPercent")}</Label>
                      <Input
                        value={engineProfile.depositCommission !== null && engineProfile.depositCommission !== undefined
                          ? engineProfile.depositCommission
                          : "N/A"}
                        disabled
                        className="bg-slate-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">{t("withdrawalCommissionPercent")}</Label>
                      <Input
                        value={engineProfile.withdrawalCommission !== null && engineProfile.withdrawalCommission !== undefined
                          ? engineProfile.withdrawalCommission
                          : "N/A"}
                        disabled
                        className="bg-slate-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">{t("percentageFeePercent")}</Label>
                      <Input
                        value={engineProfile.percentageFee !== null && engineProfile.percentageFee !== undefined
                          ? engineProfile.percentageFee
                          : "N/A"}
                        disabled
                        className="bg-slate-50"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Regenerate Keys Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <RefreshCw className="h-5 w-5" />
                {t("regenerateApiKeysTitle")}
              </CardTitle>
              <CardDescription>
                {t("regenerateApiKeysDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" onClick={() => setRegenerateDialog(true)}>
                {t("regenerateAllKeys")}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Regenerate Dialog */}
        <AlertDialog open={regenerateDialog} onOpenChange={setRegenerateDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("regenerateApiKeys")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("regenerateApiKeysConfirm")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={handleRegenerate} className="bg-red-600 hover:bg-red-700">
                {t("regenerateKeys")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </EngineLayout>
  );
}